// Apex Athletic Mind — Mindset Quiz data
// 4 archetypes, 8 questions, sport-specific scenes.

export const ARCHETYPES = {
  grinder: {
    key: 'grinder',
    name: 'The Grinder',
    headline: 'Locked in — sometimes too locked in.',
    color: '#004C54',
    description:
      "Your mind never stops working. That's your superpower AND your trap. " +
      "You analyze every play, replay every mistake, and walk into pressure carrying " +
      "the weight of every rep that came before. You don't need more focus — you need " +
      "a way to turn the volume DOWN.",
    framework_name: 'The 3-Breath Reset',
    framework_tag: 'IN-GAME RESET',
    framework_pitch:
      "30 seconds. No equipment. Anywhere. The exact tool to drop tension, " +
      "anchor your nervous system, and launch back into the moment when your mind " +
      "starts spiraling mid-game.",
    growth_line:
      "When you learn to quiet the mind on demand, your talent finally gets to play freely.",
  },
  hopeful: {
    key: 'hopeful',
    name: 'The Hopeful',
    headline: 'Talented — but waiting for permission.',
    color: '#004C54',
    description:
      "You've got the skill. You've put in the reps. But somewhere between practice " +
      "and the whistle, the belief leaks out. You walk in hoping you'll play well " +
      "instead of knowing it. You don't need more talent — you need an identity " +
      "you can stand on when the moment gets big.",
    framework_name: 'The ABC Confidence Stack',
    framework_tag: 'PRE-PERFORMANCE',
    framework_pitch:
      "90 seconds before tip / kickoff / serve. Anchor, Belief, Cue — a 3-step " +
      "ritual that walks you in with the swagger of someone who's already won.",
    growth_line:
      "Confidence isn't a feeling that shows up. It's a routine you run.",
  },
  phoenix: {
    key: 'phoenix',
    name: 'The Phoenix',
    headline: 'High highs. Brutal lows. One mistake = one bad inning.',
    color: '#004C54',
    description:
      "When you're on, you're untouchable. But one bad play, one bad call, one bad " +
      "rep, and the whole thing slides. You feel it deep. You don't need to care less — " +
      "you need a way to reset between plays so the next one doesn't carry the weight " +
      "of the last one.",
    framework_name: 'Bounce-Back 3R',
    framework_tag: 'POST-MISTAKE',
    framework_pitch:
      "30 seconds. Release, Refocus, Re-engage. The exact 3-step move to drop a " +
      "mistake and own the next play — the way the best in the world do it.",
    growth_line:
      "The next play is always bigger than the last one. Always.",
  },
  natural: {
    key: 'natural',
    name: 'The Natural',
    headline: 'Gifted, but inconsistent. No routine = no floor.',
    color: '#004C54',
    description:
      "When you're feeling it, no one can stop you. When you're not, you can't find " +
      "it. Talent shows up; consistency doesn't. You don't need to grind harder — " +
      "you need a pre-game order that loads your body, breath, and belief in the " +
      "same way every single time so 'on' becomes your default, not your gift.",
    framework_name: 'The Performance Pyramid',
    framework_tag: 'PRE-GAME ORDER',
    framework_pitch:
      "10–15 minutes pre-performance. Body → Breath → Belief. In that exact order. " +
      "The routine that turns talent into a floor you never play below.",
    growth_line:
      "Consistency isn't boring. It's how you stop leaving your A-game in the locker room.",
  },
};

// Sport-specific scene library. Each sport has one phrase per question slot (q1..q8).
// "general" is the fallback for 'other' / non-listed sports.
const SCENES = {
  basketball: {
    q1: "It's the final 8 seconds. You're at the free-throw line, down by one.",
    q2: "You just turned the ball over with a lazy pass. Coach is yelling.",
    q3: "Tip-off is in 30 minutes. You're in the locker room.",
    q4: "You just got pulled out after a rough first quarter and you're on the bench.",
    q5: "Final buzzer. You lost a close one. Walking off the floor.",
    q6: "You're lights-out in practice but freeze in real games.",
    q7: "After film, coach tells you you've been playing soft on defense.",
    q8: "Your team is down by 12 with five minutes left. The bench is quiet.",
  },
  baseball: {
    q1: "Bottom of the 7th, two outs, down by one. You're stepping into the box.",
    q2: "You just booted a routine grounder that cost a run. Coach kicks the dirt.",
    q3: "First pitch is in 30 minutes. You're in the dugout.",
    q4: "You got pulled for a pinch hitter and you're back in the dugout.",
    q5: "Game over. You went 0-for-4 with two K's. Walking to the parking lot.",
    q6: "You crush it in BP but can't find your swing in games.",
    q7: "After the game, coach says your bat speed is dropping when it counts.",
    q8: "Your team is down 6-1 in the 5th. The dugout is dead silent.",
  },
  softball: {
    q1: "Bottom of the 7th, two outs, down by one. You're stepping into the box.",
    q2: "You just booted a routine grounder that cost a run. Coach kicks the dirt.",
    q3: "First pitch is in 30 minutes. You're in the dugout.",
    q4: "You got pulled for a pinch hitter and you're back in the dugout.",
    q5: "Game over. You went 0-for-4 with two K's. Walking to the parking lot.",
    q6: "You crush it in BP but can't find your swing in games.",
    q7: "After the game, coach says your bat speed is dropping when it counts.",
    q8: "Your team is down 6-1 in the 5th. The dugout is dead silent.",
  },
  soccer: {
    q1: "Game tied. You're stepping up to take the penalty kick.",
    q2: "You just gave the ball away in your own third and they scored.",
    q3: "Kickoff is in 30 minutes. You're lacing up in the locker room.",
    q4: "You got subbed off in the 60th. You're on the bench, watching.",
    q5: "Final whistle. You lost 1-0. Walking off the pitch.",
    q6: "You're a beast in training but disappear in matches.",
    q7: "After the match, coach says you're not tracking back hard enough.",
    q8: "You're down 2-0 at halftime. The locker room is silent.",
  },
  football: {
    q1: "Fourth quarter, down by 4, 90 seconds left. You're lining up.",
    q2: "You just got beat deep for a touchdown. The crowd is loud.",
    q3: "Kickoff is in 30 minutes. You're in the locker room taping up.",
    q4: "Coach pulled you after a rough drive. You're on the sideline.",
    q5: "Final whistle. You lost a close one. Walking to the bus.",
    q6: "You crush it in practice but can't put it together on game day.",
    q7: "After film, coach says you're missing your reads.",
    q8: "You're down 21-0 at the half. The locker room feels heavy.",
  },
  volleyball: {
    q1: "Set point. You're at the service line.",
    q2: "You just shanked a pass on game point. Bench is groaning.",
    q3: "First serve is in 30 minutes. You're in the gym warming up.",
    q4: "Coach subbed you out after a few errors. You're on the bench.",
    q5: "Final point against you. Match over. Walking off.",
    q6: "You play loose in practice and tight in matches.",
    q7: "After the match, coach says your reads at the net are slow.",
    q8: "You're down 0-2 in sets. The huddle is quiet.",
  },
  tennis: {
    q1: "Match point against you. You're at the baseline about to serve.",
    q2: "You just double-faulted on a break point.",
    q3: "First serve in 30 minutes. You're stretching on the side court.",
    q4: "You're on changeover, down a set and a break.",
    q5: "Match over. You lost in three. Walking to the bench.",
    q6: "You play unreal in practice and tighten up in matches.",
    q7: "After the match, your coach says you're going for too much under pressure.",
    q8: "You're down 1-5 in the third. Sitting on changeover.",
  },
  golf: {
    q1: "Tied for the lead. You've got a 6-foot putt to win on 18.",
    q2: "You just blocked a drive OB on a par 4 you should birdie.",
    q3: "Your tee time is in 30 minutes. You're on the range.",
    q4: "You just made a double on a par 3. Walking to the next tee.",
    q5: "Final round done. You shot over par again. Walking to the car.",
    q6: "You shoot lights-out in practice rounds and balloon in tournaments.",
    q7: "After the round, your coach says you're playing scared on tight pins.",
    q8: "You're +5 through 9 in a tournament round you needed.",
  },
  track: {
    q1: "Heat seeding round. You're in the blocks for the race that matters.",
    q2: "You just false-started or popped up early out of the blocks.",
    q3: "Your event is in 30 minutes. You're stretching in the warm-up area.",
    q4: "Between heats. You ran a time that won't qualify.",
    q5: "Meet over. You ran 0.3 off your PR when it mattered. Walking to the bus.",
    q6: "You crush splits in practice and tighten up at the meet.",
    q7: "After the race, coach says your start is killing your time.",
    q8: "You're behind early in a race you need to win.",
  },
  lacrosse: {
    q1: "Game tied. 30 seconds left. You're stepping up to take the face-off.",
    q2: "You just got stripped behind the goal and they scored.",
    q3: "Faceoff in 30 minutes. You're in the locker room taping your stick.",
    q4: "Coach pulled you after a turnover. You're on the bench.",
    q5: "Final whistle. You lost a close one. Walking off the field.",
    q6: "You light it up in practice and go cold in games.",
    q7: "After film, coach says you're forcing shots from bad angles.",
    q8: "You're down 6-1 going into the fourth. Bench is dead.",
  },
  general: {
    q1: "It's the moment of the competition. The one that matters. You're about to go.",
    q2: "You just made a costly mistake everyone saw.",
    q3: "Competition starts in 30 minutes. You're getting ready.",
    q4: "You just got pulled out / subbed off after a rough start.",
    q5: "Competition is over. You didn't perform the way you wanted to.",
    q6: "You're elite in practice but freeze up in real competition.",
    q7: "Your coach just told you you're not bringing your best when it counts.",
    q8: "You're losing badly with time still on the clock.",
  },
};

// Audience pronoun helpers — keeps copy natural for athletes vs parents vs coaches.
const PRONOUN = {
  athlete: { you: 'You', your: 'Your', youre: "You're", youve: "You've" },
  parent:  { you: 'Your athlete', your: "Their", youre: "They're", youve: "They've" },
  coach:   { you: 'Your athlete', your: "Their", youre: "They're", youve: "They've" },
};

const _swap = (sentence, p) =>
  sentence
    .replaceAll("You're", p.youre).replaceAll("you're", p.youre.toLowerCase())
    .replaceAll("You've", p.youve).replaceAll("you've", p.youve.toLowerCase())
    .replaceAll("You ", `${p.you} `).replaceAll(" you ", ` ${p.you.toLowerCase()} `)
    .replaceAll("Your ", `${p.your} `).replaceAll(" your ", ` ${p.your.toLowerCase()} `);

export const getScene = (sportKey, qid, audienceKey = 'athlete') => {
  const scene = (SCENES[sportKey] || SCENES.general)[qid] || SCENES.general[qid];
  const p = PRONOUN[audienceKey] || PRONOUN.athlete;
  if (audienceKey === 'athlete') return scene;
  return _swap(scene, p);
};

// The 8 questions. Each has a stem (the prompt that follows the scene) and 4
// options, each tied to one archetype. The option text is sport-agnostic
// because it describes an INTERNAL reaction, not the situation.
export const QUESTIONS = [
  {
    id: 'q1',
    stem: "What's running through your head?",
    options: [
      { archetype: 'phoenix', text: "\"Don't blow this. Last time I missed I lost us the game.\"" },
      { archetype: 'grinder', text: "Running through technique, form, every variable. Mind racing." },
      { archetype: 'hopeful', text: "\"I hope I don't let everyone down right now.\"" },
      { archetype: 'natural', text: "Honestly? Not thinking much. Just gonna go." },
    ],
  },
  {
    id: 'q2',
    stem: "What's the next thing you do?",
    options: [
      { archetype: 'phoenix', text: "Carry it. That play replays in your head for the next 5 minutes." },
      { archetype: 'grinder', text: "Over-analyze what went wrong. Try to fix it mid-game." },
      { archetype: 'hopeful', text: "Shrink. Play smaller. Avoid the ball / the moment." },
      { archetype: 'natural', text: "Shake it off and hope the next one feels better." },
    ],
  },
  {
    id: 'q3',
    stem: "What does the pre-game look like?",
    options: [
      { archetype: 'natural', text: "Whatever. Music, vibes, send it. No real routine." },
      { archetype: 'grinder', text: "Locked in. Reviewing notes. Running through every scenario." },
      { archetype: 'hopeful', text: "Nervous. Hyped one minute, doubting yourself the next." },
      { archetype: 'phoenix', text: "Depends on the day. Sometimes electric, sometimes flat." },
    ],
  },
  {
    id: 'q4',
    stem: "What's the inner monologue?",
    options: [
      { archetype: 'phoenix', text: "\"This is over. We're done. I let the team down.\"" },
      { archetype: 'grinder', text: "Replaying every mistake. Hunting for the technical fix." },
      { archetype: 'hopeful', text: "\"Coach is gonna stop trusting me.\"" },
      { archetype: 'natural', text: "\"I'll get hot eventually. Always do.\"" },
    ],
  },
  {
    id: 'q5',
    stem: "How do you carry it?",
    options: [
      { archetype: 'phoenix', text: "Heavy. The loss eats at you for hours, maybe days." },
      { archetype: 'grinder', text: "Mentally rerun every key moment looking for what to fix." },
      { archetype: 'hopeful', text: "Quiet. Wonder if you've got what it takes." },
      { archetype: 'natural', text: "On to the next one. Don't dwell — just move." },
    ],
  },
  {
    id: 'q6',
    stem: "What does that feel like?",
    options: [
      { archetype: 'hopeful', text: "Like the moment is bigger than you. Tight. Wanting permission." },
      { archetype: 'grinder', text: "Like there are 20 thoughts competing at the same time." },
      { archetype: 'phoenix', text: "Like everything is amplified — the highs and the misses." },
      { archetype: 'natural', text: "Like you forgot how you do this for a sec. Body feels off." },
    ],
  },
  {
    id: 'q7',
    stem: "What's your first reaction?",
    options: [
      { archetype: 'hopeful', text: "Take it to heart. Now you're second-guessing every move." },
      { archetype: 'grinder', text: "Already breaking it down: \"Here's exactly what I'll change.\"" },
      { archetype: 'phoenix', text: "Sting. You're embarrassed and fired up at the same time." },
      { archetype: 'natural', text: "Nod. Move on. Trust it'll click." },
    ],
  },
  {
    id: 'q8',
    stem: "What's your move?",
    options: [
      { archetype: 'phoenix', text: "Get loud or get quiet — you go to extremes when it slips." },
      { archetype: 'grinder', text: "Try to do everyone's job. Over-effort. Force plays." },
      { archetype: 'hopeful', text: "Shrink. Wait for someone else to start the comeback." },
      { archetype: 'natural', text: "Stay loose. Wait for the wave to flip." },
    ],
  },
];

// The full list of sport options shown in the picker.
export const SPORTS = [
  { key: 'basketball', label: 'Basketball' },
  { key: 'baseball',   label: 'Baseball' },
  { key: 'softball',   label: 'Softball' },
  { key: 'soccer',     label: 'Soccer' },
  { key: 'football',   label: 'Football' },
  { key: 'volleyball', label: 'Volleyball' },
  { key: 'tennis',     label: 'Tennis' },
  { key: 'golf',       label: 'Golf' },
  { key: 'track',      label: 'Track & Field' },
  { key: 'lacrosse',   label: 'Lacrosse' },
  { key: 'general',    label: 'Other / general athlete' },
];

export const AUDIENCES = [
  { key: 'athlete', label: 'I\u2019m the athlete' },
  { key: 'parent',  label: 'I\u2019m a parent' },
  { key: 'coach',   label: 'I\u2019m a coach' },
];

// Helper — given an array of selected archetype keys (one per question),
// return the winning archetype and the scores breakdown.
export function scoreQuiz(selectedArchetypes) {
  const scores = { grinder: 0, hopeful: 0, phoenix: 0, natural: 0 };
  selectedArchetypes.forEach((k) => { if (scores[k] != null) scores[k]++; });
  // Tie-break: use the LAST answered question's archetype (the "real-game" feel).
  let winner = null;
  let max = -1;
  Object.entries(scores).forEach(([k, v]) => {
    if (v > max) { max = v; winner = k; }
  });
  // If multiple tied at max, prefer the last selected one among them
  const tied = Object.entries(scores).filter(([, v]) => v === max).map(([k]) => k);
  if (tied.length > 1) {
    for (let i = selectedArchetypes.length - 1; i >= 0; i--) {
      if (tied.includes(selectedArchetypes[i])) { winner = selectedArchetypes[i]; break; }
    }
  }
  return { winner, scores };
}
